import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ILocationTransferPair from '~/client/src/shared/models/ILocationTransferPair'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Workflow from '~/client/src/shared/models/Workflow'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import { MaterialsDataKeys } from '../MaterialsDataKeys'
import { IFormCellData } from '../MaterialsList.store'
import BaseDataCell from './BaseDataCell/BaseDataCell'
import MaterialTransferCellTable from './MaterialTransferCellTable'

// localization: translated

interface IProps {
  dataKey: string
  formCellData: IFormCellData
  isTransferEnabled: boolean
  transferFormIconName: string
  onNewTransferClick(
    viewModelId: string,
    selectedLocationsMap: Map<string, ILocationTransferPair>,
  ): void
  onTransferIdClick(form: Workflow): void
  locationRenderer(location: LocationBase): JSX.Element

  projectDateStore?: ProjectDateStore
}

const transferCellColumnsIds = [
  MaterialsDataKeys.MATERIAL_TRANSFER_DATE,
  MaterialsDataKeys.PLANNED_QUANTITY,
  MaterialsDataKeys.STATUS,
  MaterialsDataKeys.CURRENT_MATERIAL_LOCATION,
  MaterialsDataKeys.PLANNED_INSTALL_LOCATION,
]

const isFormDone = (form: Workflow): boolean => form.isDone
const isFormInRange = (
  form: Workflow,
  startDate: Date,
  endDate: Date,
): boolean => form.isScheduledWithinRange(startDate, endDate)

@inject('projectDateStore')
@observer
export default class MaterialTransferCell extends React.Component<IProps> {
  public static defaultProps = {
    formCellData: {},
  }

  @observable private localProps: IProps

  public constructor(props: IProps) {
    super(props)
    this.localProps = props

    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const { formCellData, isTransferEnabled, transferFormIconName } = this.props
    const { transferForms, itemsCount } = formCellData

    return (
      <BaseDataCell<Workflow>
        items={transferForms}
        newItemIcon={
          <WorkflowTypeIcon
            iconName={transferFormIconName}
            className="form-id-icon-large relative no-flex mr5"
          />
        }
        newItemText={Localization.translator.newTransfer}
        onNewItemClick={this.onNewTransferClick}
        renderActiveItems={this.renderActiveFormsTable}
        renderDoneItems={this.renderDoneFormsTable}
        doneFilter={isFormDone}
        dateRangeFilter={isFormInRange}
        isNewItemBtnHidden={!isTransferEnabled}
        isNewValueShown={this.isTransferIdCell && isTransferEnabled}
        isTotalDoneValueShown={this.isTransferIdCell}
        isDateRangeShown
        doneTableText={Localization.translator.doneTransfers}
        getTotalDoneValue={this.getTotalQuantity}
      >
        <div
          className={classList({
            'row pointer h48 no-select': true,
            'underline-hover': !!itemsCount,
          })}
        >
          <div className="text-ellipsis">{this.cellLabel}</div>
          <ChevronDownIcon size={12} className="ml5" />
        </div>
      </BaseDataCell>
    )
  }

  private get cellLabel(): string | JSX.Element {
    const { itemsCount } = this.props.formCellData

    if (!itemsCount) {
      return NO_VALUE
    }
    return itemsCount > 1 ? this.xItemsLabel : this.oneItemLabel
  }

  private get xItemsLabel(): string {
    const {
      formCellData: { itemsCount },
      dataKey,
    } = this.props
    switch (dataKey) {
      case MaterialsDataKeys.MATERIAL_TRANSFER_ID:
        return Localization.translator.xTransfers(itemsCount)
      case MaterialsDataKeys.MATERIAL_TRANSFER_DATE:
        return Localization.translator.xDates(itemsCount)
      default:
        return null
    }
  }

  private get oneItemLabel(): JSX.Element | string {
    const {
      projectDateStore: { getMonthDayYearAndTimeToDisplay },
      dataKey,
    } = this.props

    const form = this.allActiveForms[0]
    switch (dataKey) {
      case MaterialsDataKeys.MATERIAL_TRANSFER_ID:
        return form.code
      case MaterialsDataKeys.MATERIAL_TRANSFER_DATE:
        return getMonthDayYearAndTimeToDisplay(form.startDate)
      default:
        return null
    }
  }

  private renderActiveFormsTable = (activeForms: Workflow[]): JSX.Element => {
    const {
      formCellData,
      locationRenderer,
      onTransferIdClick,
      transferFormIconName,
    } = this.localProps
    return (
      <MaterialTransferCellTable
        isFooterHidden={!this.isTransferIdCell}
        shownColumnIds={this.shownColumnIds}
        forms={activeForms}
        procurementId={formCellData.procurementId}
        materialId={formCellData.materialId}
        transferFormIconName={transferFormIconName}
        footerTitle={Localization.translator.totalBooked}
        locationRenderer={locationRenderer}
        onIdClick={onTransferIdClick}
      />
    )
  }

  private renderDoneFormsTable = (doneForms: Workflow[]): JSX.Element => {
    const {
      formCellData,
      transferFormIconName,
      locationRenderer,
      onTransferIdClick,
    } = this.localProps

    return (
      <MaterialTransferCellTable
        isHeaderHidden
        isFooterHidden={!this.isTransferIdCell}
        shownColumnIds={this.shownColumnIds}
        forms={doneForms}
        procurementId={formCellData.procurementId}
        materialId={formCellData.materialId}
        transferFormIconName={transferFormIconName}
        footerTitle={Localization.translator.totalTransferred}
        locationRenderer={locationRenderer}
        onIdClick={onTransferIdClick}
      />
    )
  }

  private get allActiveForms(): Workflow[] {
    return this.props.formCellData.transferForms.filter(f => !f.isDone)
  }

  private getTotalQuantity = (doneForms: Workflow[]): React.ReactText => {
    const { procurementId, materialId } = this.localProps.formCellData
    return doneForms.reduce(
      (sum, form) =>
        (sum +=
          (procurementId
            ? form.procurementQuantitiesMap[procurementId]
            : form.materialQuantitiesMap[materialId]) || 0),
      0,
    )
  }

  private get shownColumnIds(): string[] {
    return this.isTransferIdCell
      ? transferCellColumnsIds
      : [this.localProps.dataKey]
  }

  private get isTransferIdCell(): boolean {
    return this.localProps.dataKey === MaterialsDataKeys.MATERIAL_TRANSFER_ID
  }

  private onNewTransferClick = () => {
    const { onNewTransferClick, formCellData } = this.props
    const pair: ILocationTransferPair = { location: null, quantity: null }
    onNewTransferClick(
      formCellData.id,
      new Map<string, ILocationTransferPair>([[null, pair]]),
    )
  }
}
